import AdminPage from '../../components/admin/AdminPage';
import React, { useCallback, useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
} from 'reactstrap';
import AdminUploadFile from '../../components/admin/AdminUploadFile';
import { FilePreviewTypes } from '../../constants/formConstants';
import useAuth from '../../hooks/useAuth';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createModelUnitsInBulk,
  createModelUnitsTakenNotTakenInBulk,
} from './services/investorService';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';

const csvExampleColumns = [
  '',
  'name',
  'base_price',
  'area',
  'square_rods',
  'bathrooms',
  'bedrooms',
  'parking',
  'level',
  'distribution',
];

// The first column has no header and holds an incremental index (0, 1, 2, ...).
const csvExampleRows = [
  ['0', '146', '62490', '69.78', '', '1', '3', '1', '0', ''],
  ['1', '144', '62490', '69.78', '', '1', '3', '1', '0', ''],
  ['2', '142', '62490', '69.78', '', '1', '3', '1', '0', ''],
];

const ModelUnitBulkCreate: React.FC = () => {
  const { id: modelId = '' } = useParams();
  const { callEndpoint, isLoading } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>('csv');
  const [errorAlert, setErrorAlert] = useState<string>('');
  const globalAlertOptions = {
    success: false,
    warning: true,
    onConfirm: () => setErrorAlert(''),
    title: 'Error',
    text: errorAlert || '',
  };

  const pageName = 'Crear unidades a través de CSV';
  const parentPageName = 'Modelo';

  const downloadCSVExample = useCallback(() => {
    const csvContent = [
      csvExampleColumns.join(','),
      ...csvExampleRows.map((row) => row.join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'ejemplo-unidades.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }, []);

  const uploadCSVForProcessing = useCallback(async (files: Array<any>) => {
    if (!files.length) {
      setErrorAlert('Error, debes de adjuntar un archivo para subir');
      return;
    }
    const formData = new FormData();
    formData.append('model_unit_file', files[0]);
    const { status } = await callEndpoint(
      createModelUnitsInBulk(formData, modelId, token),
    );
    if (status === 201) {
      navigate(`/investors/models/${modelId}/model-units`); //make a reload
    } else {
      setErrorAlert(
        'Lo sentimos, por el momento no es posible completar la acción',
      );
    }
  }, []);

  const uploadCSVTakenNotTaken = useCallback(async (files: Array<any>) => {
    if (!files.length) {
      setErrorAlert('Error, debes de adjuntar un archivo para subir');
      return;
    }
    const formData = new FormData();
    formData.append('model_unit_file', files[0]);
    const { status } = await callEndpoint(
      createModelUnitsTakenNotTakenInBulk(formData, modelId, token),
    );
    navigate(`/investors/models/${modelId}/model-units`); //make a reload
  }, []);

  return (
    <AdminPage
      name={pageName}
      parentName={parentPageName}
      globalAlertOptions={globalAlertOptions}
    >
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="bg-light">
              <Nav
                className="nav-fill flex-column flex-md-row"
                id="tabs-upload-model-units"
                pills
                role="tablist"
              >
                <NavItem>
                  <NavLink
                    className={activeTab === 'csv' ? 'active' : ''}
                    onClick={() => setActiveTab('csv')}
                    style={{ cursor: 'pointer' }}
                    role="tab"
                  >
                    <i className="fa fa-duotone fa-file-csv mr-2" />
                    Subir CSV
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === 'taken-not-taken' ? 'active' : ''}
                    onClick={() => setActiveTab('taken-not-taken')}
                    style={{ cursor: 'pointer' }}
                    role="tab"
                  >
                    <i className="fa fa-duotone fa-users mr-2" />
                    Subir CSV con asignación de usuarios
                  </NavLink>
                </NavItem>
              </Nav>
            </CardHeader>
            <CardBody>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="csv">
                  <p className="text-muted mb-3">
                    El archivo CSV debe incluir las siguientes columnas. A
                    continuación un ejemplo del formato esperado:
                  </p>
                  <div className="table-responsive mb-4">
                    <Table className="align-items-center table-sm" bordered>
                      <thead className="thead-light">
                        <tr>
                          {csvExampleColumns.map((column, columnIndex) => (
                            <th key={columnIndex} className="text-nowrap">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvExampleRows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="text-nowrap">
                                {cell === '' ? '—' : cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <AdminUploadFile
                    handleUpload={uploadCSVForProcessing}
                    description={'Subir CSV'}
                    dropZoneText={'Subir CSV'}
                    maxFiles={1}
                    filesPreviewType={FilePreviewTypes.COUNT}
                    onTemplateDownload={downloadCSVExample}
                    isLoading={isLoading}
                  />
                </TabPane>
                <TabPane tabId="taken-not-taken">
                  <p className="text-muted mb-3">
                    Usa esta opción para crear unidades incluyendo la asignación
                    de usuarios (taken / not taken). Descarga la plantilla desde
                    el enlace "Ver plantilla".
                  </p>
                  <AdminUploadFile
                    handleUpload={uploadCSVTakenNotTaken}
                    description={'Subir CSV con asignación de usuarios '}
                    dropZoneText={`Subir CSV (versión taken not taken)`}
                    maxFiles={1}
                    filesPreviewType={FilePreviewTypes.COUNT}
                    templateUrl="https://storage.googleapis.com/assets-us-east4-propilatam/admin/templates/taken-not-taken%20-%20template.csv"
                    isLoading={isLoading}
                  />
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </AdminPage>
  );
};

export default ModelUnitBulkCreate;
